import { useEffect, useRef, useState, useMemo } from "react";
import { useExamList, useGrades, useGradeSubjects, useUserInfo } from "../../react-query/queries";
import { useAuth } from "../../utils/Auth/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, ChevronDown, Clock, Eye, FileCheck2, FileText, Layers, Pencil, Plus, Search, Shuffle, Sparkles, Trash2 } from "lucide-react";
import { canUseMixedExam } from "../../school_panel/lib/mixedExam";
import { AdminAcademicYear, AdminField, adminGetFieldAcademicYears, adminGetInstitutionFields, adminGetAcademicYearCourses, adminGetExams, deleteExam } from "../../utils/remote";
import { scopeLabel } from './ExamAssignmentInfo';
import type { ExamAssignmentScope, ExamDelivery } from '../../school_panel/api/types';
import { Badge, Button, Chip, ConfirmDialog, EmptyState, KpiCard, PageHeader, ErrorPanel, PanelCard, Pager, Segmented, Spinner, Table, Tag, Td, Term, Th, Track } from '../../school_panel/components/ui';
import ExamSetsCard from './ExamSetsCard';
import { useTooltip } from '../../school_panel/components/ui/Tooltip';
import KindBadge from '../../pages/Worksheets/components/KindBadge';
import { withTime } from '../../school_panel/lib/dates';
import { useOtherSchoolGuard } from "./useOtherSchoolGuard";

/** Strip a leading "Grade " so callers can prefix it once ("Grade Grade 9" bug). */
const stripGradePrefix = (name: unknown) => String(name ?? '').trim().replace(/^grade\s+/i, '');

/** Icon-only row action: aria-label for screen readers plus a hover/focus tooltip. */
function RowAction({ label, onClick, hoverClass, children }: { label: string; onClick: () => void; hoverClass: string; children: React.ReactNode }) {
    const { ref, triggerProps, tooltip } = useTooltip<HTMLButtonElement>(label);
    return (
        <>
            <button
                ref={ref}
                type="button"
                aria-label={label}
                onClick={onClick}
                {...triggerProps}
                className={`flex h-9 w-9 items-center justify-center rounded-[10px] border border-transparent text-muted-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent ${hoverClass}`}
            >
                {children}
            </button>
            {tooltip}
        </>
    );
}

type QExam = {
    id: number,
    name: string,
    no_ques: string,
    price: string,
    status: string,
    sub: string,
    time: number,
    time_given: number,
    grade: string,
    institution_name?: string,
    institution_id?: number | null,
    delivery?: ExamDelivery,
    /** Written answers still to mark for the viewer (school exam list only). */
    to_mark?: number,
}

const FIELD_WRAP = 'flex min-h-[44px] min-w-[150px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent';
const FIELD_INPUT = 'min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-muted disabled:cursor-not-allowed disabled:opacity-60';
const FIELD_LABEL = 'whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted';

const NEW_EXAM_ITEMS = [
    { label: 'Blank exam', to: '/exams/add-exam', hint: 'Write or import questions yourself', icon: Pencil, hot: false },
    { label: 'Generate with AI', to: '/exams/generate', hint: 'Draft a full exam from your chapters', icon: Sparkles, hot: true },
    { label: 'Mixed exam', to: '/exams/mixed-exam', hint: 'Pick questions from national exams', icon: Shuffle, hot: false },
    { label: 'Paper exam', to: '/exams/paper/new', hint: 'Print answer sheets, scan to mark', icon: FileCheck2, hot: false },
];

type ExamCounts = { active: number; draft: number; due_this_week: number; past_due: number };
type NextDue = { id: number; name: string; due_at: string };
type RecentAvg = { days: number; avg_score: number | null; delta: number | null; exams_with_results: number };

/** Status filter tabs of the exam list: key, label, and the query params it sends. */
const STATUS_TABS = [
    { key: 'all', label: 'All', filters: undefined },
    { key: 'active', label: 'Active', filters: { status: 'Active' } },
    { key: 'draft', label: 'Draft', filters: { status: 'Draft' } },
    { key: 'this_week', label: 'Due this week', filters: { due: 'this_week' } },
    { key: 'past', label: 'Past due', filters: { due: 'past' } },
] as const;
type StatusKey = typeof STATUS_TABS[number]['key'];

const DAY_MS = 24 * 60 * 60 * 1000;

export type ListKind = 'exam' | 'worksheet' | 'homework';

/** Wording and links of the Exams, Worksheets and Homework pages (one list component, one kind each). */
const KIND_COPY: Record<ListKind, { eyebrow: string; title: string; one: string; many: string; region: string; newTo: string; newLabel: string }> = {
    exam: { eyebrow: 'Academics · Exams', title: 'Exam list', one: 'exam', many: 'exams', region: 'Exams', newTo: '/exams/add-exam', newLabel: 'New exam' },
    worksheet: { eyebrow: 'Academics · Worksheets', title: 'Worksheets', one: 'worksheet', many: 'worksheets', region: 'Worksheets', newTo: '/exams/add-exam?kind=worksheet', newLabel: 'New worksheet' },
    homework: { eyebrow: 'Academics · Homework', title: 'Homework', one: 'homework', many: 'homework', region: 'Homework', newTo: '/exams/add-exam?kind=homework', newLabel: 'New homework' },
};

/** Parse a due value (ISO datetime or YYYY-MM-DD) into a Date, or null. */
function parseDue(dueAt: unknown, dueDate: unknown): Date | null {
    if (dueAt) {
        const d = new Date(String(dueAt));
        return Number.isNaN(d.getTime()) ? null : d;
    }
    if (dueDate) {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dueDate));
        const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59) : new Date(String(dueDate));
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
}

/** "New exam" primary action with a small menu of the ways to start one. */
/** `mixed`: offer Mixed exam, which only works for YADA staff, Hub admins and colleges/universities. */
function NewExamMenu({ mixed }: { mixed: boolean }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={wrapRef} className="relative inline-flex">
            <Link
                to="/exams/add-exam"
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-l-[12px] border border-accent bg-accent px-4 py-2 text-sm font-semibold text-navy transition hover:brightness-105 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <Plus size={18} strokeWidth={1.8} aria-hidden="true" />New exam
            </Link>
            <button
                type="button"
                aria-label="More ways to create an exam"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-r-[12px] border border-l-0 border-accent bg-accent px-3 text-navy shadow-split transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <ChevronDown size={18} strokeWidth={1.8} aria-hidden="true" />
            </button>
            {open && (
                <div role="menu" aria-label="Create an exam" className="absolute right-0 top-full z-20 mt-2 w-[300px] max-w-[calc(100vw-32px)] rounded-[16px] border border-line-strong bg-surface p-2 shadow-menu">
                    {NEW_EXAM_ITEMS.filter((item) => mixed || item.to !== '/exams/mixed-exam').map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            role="menuitem"
                            onClick={() => setOpen(false)}
                            className="flex items-start gap-3 rounded-[12px] p-2.5 text-ink hover:bg-note hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                            <span className={`grid h-9 w-9 flex-none place-items-center rounded-[11px] ${item.hot ? 'bg-accent text-navy' : 'bg-mist text-slate-ink'}`}>
                                <item.icon size={18} strokeWidth={1.8} aria-hidden="true" />
                            </span>
                            <span>
                                <span className="block font-semibold">{item.label}</span>
                                <span className="block text-[13px] text-muted">{item.hint}</span>
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function ExamListComponent({ kind = 'exam' }: { kind?: ListKind } = {}) {
    const copy = KIND_COPY[kind];
    const { token, role } = useAuth();
    const { data: userInfo } = useUserInfo();
    const [nowGrade, setNowGrade] = useState(0)
    const [nowSubject, setNowSubject] = useState('0')
    const [currentPage, setCurrentPage] = useState(1);
    const onPageChange = (page: number) => setCurrentPage(page);
    const navigate = useNavigate();

    const isAllowed = !!token && (
        role === 'superuser' || role === 'school' || role === 'college_university'
        || role === 'admin' || role === 'school_admin' || role === 'teacher'
    );

    const { data: gradeData } = useGrades(token)
    const { data: subjectData, isFetched: subjectIsFetched } = useGradeSubjects(token, nowGrade.toString())
    const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
    const [search, setSearch] = useState('');
    const [statusKey, setStatusKey] = useState<StatusKey>('all');
    const statusFilters = STATUS_TABS.find((t) => t.key === statusKey)?.filters;
    // One data source per role across every tab. The institution list is for school admins and for
    // superusers with an institution only (it answers 403 to everyone else, including the legacy
    // `school` / `college_university` roles); every other role reads the school exam list, which carries the same status/due filters, counts and
    // row figures. The other query is never enabled (a null page disables it).
    const adminSource = isAllowed && !!userInfo?.institution_id && (role === 'school_admin' || role === 'superuser');
    const { status: schoolStatus, data: schoolData, error: schoolErrorObj, isFetching: schoolFetching, refetch: examRefetch } = useExamList(token, adminSource || userInfo === undefined ? null : `${currentPage}`, nowGrade.toString(), nowSubject.toString(), search, { ...statusFilters, kind })
    const [adminExamsData, setAdminExamsData] = useState<{ exams: any[]; total_count: number; counts?: ExamCounts; next_due?: NextDue | null; recent_avg?: RecentAvg } | null>(null);
    const [adminFetching, setAdminFetching] = useState(false);
    const [adminError, setAdminError] = useState<false | 'forbidden' | 'failed'>(false);
    // getExamsList resolves with the Error object on a failed request, so a missing `exams` list is a failure too.
    const schoolError = schoolStatus === 'error' || (!!schoolData && !Array.isArray((schoolData as { exams?: unknown }).exams));
    const schoolForbidden = (schoolData as { response?: { status?: number } } | undefined)?.response?.status === 403
        || (schoolStatus === 'error' && (schoolErrorObj as { response?: { status?: number } } | null)?.response?.status === 403);
    const forbidden = adminSource ? adminError === 'forbidden' : schoolForbidden;
    const listFailed = adminSource ? adminError !== false : schoolError;
    const examsData = adminSource ? adminExamsData : (schoolError ? undefined : schoolData);
    const examsStatus = listFailed ? 'error' : adminSource ? (adminExamsData ? 'success' : 'pending') : schoolStatus;
    const examsIsFetching = adminSource ? adminFetching : schoolFetching;


    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    // A superuser is asked once before the first change to another school's exam.
    const { guard: otherSchoolGuard, dialog: otherSchoolDialog } = useOtherSchoolGuard();
    const [examToDelete, setExamToDelete] = useState<QExam | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Filter states for university flow
    const [filterAcademicYears, setFilterAcademicYears] = useState<AdminAcademicYear[] | null>(null);
    const [filterCourses, setFilterCourses] = useState<{ id: number; name: string }[] | null>(null);
    const [filterAcademicYearId, setFilterAcademicYearId] = useState<number | null>(null);

    const isUniversityFlow = useMemo(() => {
        const instType = userInfo?.institution_type || (role === 'college_university' ? 'University/College' : (role === 'school' ? 'School' : null));
        return instType && (instType.toLowerCase().includes('university') || instType.toLowerCase().includes('college'));
    }, [userInfo, role]);

    const activeExams = useMemo(() => (examsData as { exams?: any[] } | null | undefined)?.exams ?? [], [examsData]);

    // Library aggregates ride on the same response as the rows (absent on older backends: seg and KPIs hide).
    const counts = (examsData as { counts?: ExamCounts } | null | undefined)?.counts;
    const nextDue = (examsData as { next_due?: NextDue | null } | null | undefined)?.next_due ?? null;
    const recentAvg = (examsData as { recent_avg?: RecentAvg } | null | undefined)?.recent_avg;

    // B-017 (UI mitigation): exams owned by another institution are shared and
    // read-only; shared drafts from other schools are hidden entirely.
    const myInstitutionId = userInfo?.institution_id ?? null;
    const isSharedExam = (exam: any) =>
        role !== 'superuser'
        && myInstitutionId != null
        && exam.institution_id != null
        && Number(exam.institution_id) !== Number(myInstitutionId);
    const visibleExams = useMemo(
        () => activeExams.filter((exam: any) => !(isSharedExam(exam) && String(exam.status ?? '').toLowerCase() === 'draft')),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeExams, myInstitutionId, role],
    );

    const totalExamsCount = useMemo(() => (examsData as { total_count?: number } | null | undefined)?.total_count ?? 0, [examsData]);

    useEffect(() => {
        if (isAllowed && !adminSource && userInfo !== undefined) {
            examRefetch();
        }
    }, [currentPage, nowGrade, nowSubject, search, isAllowed, statusKey, kind])

    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        if (isUniversityFlow && instId) {
            (async () => {
                try {
                    const fetched = await adminGetInstitutionFields(instId);
                    if (mounted) setFieldsData(fetched);
                } catch (err) {
                    
                    if (mounted) setFieldsData([]);
                }
            })();
        } else {
            setFieldsData(null);
        }
        return () => { mounted = false; }
    }, [isUniversityFlow, userInfo]);

    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        if (fieldsData != null && nowGrade && instId) {
            (async () => {
                try {
                    const years = await adminGetFieldAcademicYears(instId, nowGrade);
                    if (mounted) {
                        setFilterAcademicYears(years);
                        setFilterAcademicYearId(null);
                    }
                } catch (err) {
                    
                    if (mounted) setFilterAcademicYears([]);
                }
            })();
        } else {
            setFilterAcademicYears(null);
            setFilterAcademicYearId(null);
            setFilterCourses(null);
        }
        return () => { mounted = false; }
    }, [fieldsData, nowGrade, userInfo]);

    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        if (adminSource && instId) {
            setAdminFetching(true);
            setAdminError(false);
            (async () => {
                try {
                    const pageNum = Number(currentPage) || 1;
                    const gradeParam = nowGrade && nowGrade !== 0 ? nowGrade : null;
                    const subjectParam = nowSubject && nowSubject !== '0' ? Number(nowSubject) : null;
                    const isUni = fieldsData != null;
                    const res = await adminGetExams({ 
                        page: pageNum, 
                        page_size: 10, 
                        search: search || undefined, 
                        institution: instId, 
                        grade: isUni ? null : (gradeParam ?? null),
                        field_of_study: isUni ? (gradeParam ?? null) : null,
                        subject: subjectParam ?? null,
                        sort: '-created',
                        withStats: true,
                        kind,
                        ...(statusFilters && 'status' in statusFilters ? { status: statusFilters.status as 'Active' | 'Draft' } : {}),
                        ...(statusFilters && 'due' in statusFilters ? { due: statusFilters.due as 'this_week' | 'past' } : {}),
                    });
                    if (!mounted) return;
                    const results = Array.isArray(res.results) ? res.results : (res.results || []);
                    const mapped = (results || []).map((e: any) => ({
                        id: e.id,
                        name: e.name,
                        sub: e.subject_name ?? e.course_name ?? '',
                        no_ques: e.no_ques ?? 0,
                        time_given: e.time_given ?? null,
                        grade: e.grade_name ?? e.field_of_study_name ?? '',
                        status: e.status,
                        institution_name: e.institution_name ?? '',
                        institution_id: e.institution_id ?? null,
                        delivery: e.delivery,
                        kind: e.kind,
                        due_date: e.due_date ?? null,
                        due_at: e.due_at ?? null,
                        assignment_scope: e.assignment_scope ?? null,
                        assigned_student_ids: e.assigned_student_ids ?? [],
                        is_generated: e.is_generated,
                        took: e.took,
                        assigned: e.assigned,
                        avg_score: e.avg_score,
                        exam_set: e.exam_set ?? null,
                        to_mark: e.to_mark ?? 0,
                        owner_school: e.owner_school ?? null,
                    }));
                    setAdminExamsData({ exams: mapped, total_count: typeof res.count === 'number' ? res.count : mapped.length, counts: res.counts, next_due: res.next_due, recent_avg: res.recent_avg });
                } catch (err) {
                    
                    if (mounted) setAdminError((err as { response?: { status?: number } })?.response?.status === 403 ? 'forbidden' : 'failed');
                } finally {
                    if (mounted) setAdminFetching(false);
                }
            })();
        } else {
            setAdminExamsData(null);
            setAdminError(false);
        }
        return () => { mounted = false; }
    }, [fieldsData, nowGrade, filterAcademicYearId, nowSubject, currentPage, search, userInfo, isAllowed, adminSource, refreshKey, statusKey, kind]);

    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        if (fieldsData != null && nowGrade && filterAcademicYearId && instId) {
            (async () => {
                try {
                    const courses = await adminGetAcademicYearCourses(instId, nowGrade, filterAcademicYearId);
                    if (mounted) {
                        setFilterCourses(courses);
                        setNowSubject('0');
                    }
                } catch (err) {
                    
                    if (mounted) setFilterCourses([]);
                }
            })();
        } else {
            setFilterCourses(null);
            setNowSubject('0');
        }
        return () => { mounted = false; }
    }, [filterAcademicYearId, fieldsData, nowGrade, userInfo]);

    const refreshList = () => { setRefreshKey((k) => k + 1); if (!adminSource) examRefetch(); };

    const handleDeleteExam = async () => {
        if (!examToDelete) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await deleteExam(token, examToDelete.id);
            setIsDeleteModalOpen(false);
            setExamToDelete(null);
            refreshList();
        } catch (error) {
            setIsDeleteModalOpen(false);
            setDeleteError(`Could not delete "${examToDelete.name}". Try again.`);
        } finally {
            setIsDeleting(false);
        }
    };

    const libraryTotal = counts ? counts.active + counts.draft : totalExamsCount;
    const header = (
        <PageHeader
            eyebrow={copy.eyebrow}
            title={copy.title}
            subtitle={isAllowed
                ? (counts
                    ? `${libraryTotal} ${copy.many} in your library \u00b7 ${counts.active} active, ${counts.draft} ${counts.draft === 1 ? 'draft' : 'drafts'} \u00b7 ${counts.due_this_week} due this week`
                    : `${totalExamsCount} ${copy.many} in your library`)
                : undefined}
            actions={isAllowed ? (kind === 'exam' ? (
                <>
                    <Button variant="ghost" onClick={() => navigate('/exams/generate')}><Sparkles size={18} strokeWidth={1.8} aria-hidden="true" />Generate with AI</Button>
                    <Button variant="ghost" onClick={() => navigate('/exams/paper')}><FileCheck2 size={18} strokeWidth={1.8} aria-hidden="true" />Paper exams</Button>
                    <NewExamMenu mixed={canUseMixedExam(role, userInfo)} />
                </>
            ) : (
                <Link to={copy.newTo} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-accent bg-accent px-4 py-2 text-sm font-semibold text-navy no-underline transition hover:brightness-105 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    <Plus size={18} strokeWidth={1.8} aria-hidden="true" />{copy.newLabel}
                </Link>
            )) : undefined}
        />
    );

    if (!isAllowed) {
        return (
            <>
                {header}
                <div role="alert" className="rounded-[12px] bg-danger-bg p-4 text-danger-ink">You are not authorised to view {copy.many}.</div>
            </>
        );
    }

    const gradeOptions: { key: string; label: string }[] = [
        { key: 'all', label: 'All' },
        ...(fieldsData != null
            ? fieldsData.map((f: AdminField) => ({ key: String(f.id), label: f.name }))
            : (gradeData?.grades ?? []).map((grade: any) => ({ key: String(grade.id), label: `Grade ${stripGradePrefix(grade.grade_name)}` }))),
    ];
    const onGradeChange = (key: string) => {
        setCurrentPage(1);
        setNowSubject('0');
        if (key === 'all') {
            setNowGrade(0);
            setFilterAcademicYearId(null);
        } else {
            setNowGrade(Number(key));
        }
    };

    const totalPages = Math.ceil(totalExamsCount / 10) || 1;
    const sevenDaysOut = Date.now() + 7 * DAY_MS;
    const startOfToday = new Date().setHours(0, 0, 0, 0);

    const clearFilters = () => {
        setCurrentPage(1);
        setSearch('');
        setStatusKey('all');
        onGradeChange('all');
    };
    const avgNow = recentAvg?.avg_score ?? null;
    const avgDelta = recentAvg?.delta ?? null;
    // "next Wed" only while the exam is within a week; further out the chip shows its date.
    const nextDueDate = nextDue ? new Date(nextDue.due_at) : null;
    const nextDueDay = nextDueDate && !Number.isNaN(nextDueDate.getTime())
        ? (nextDueDate.getTime() - Date.now() < 7 * DAY_MS
            ? `next ${nextDueDate.toLocaleDateString(undefined, { weekday: 'short' })}`
            : nextDueDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }))
        : null;

    return (
        <>
            {header}

            {counts && (
                <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-4">
                    <KpiCard label="Active" size="lg" value={counts.active} sub="Visible to students now" />
                    <KpiCard label="Drafts" size="lg" value={counts.draft} sub="Not yet published" />
                    <KpiCard
                        label="Due this week"
                        size="lg"
                        accentBorder
                        value={counts.due_this_week}
                        chip={nextDueDay ? <Chip tone="accent">{nextDueDay}</Chip> : undefined}
                        sub={nextDue ? `${nextDue.name} is next` : 'Nothing upcoming'}
                    />
                    <KpiCard
                        label="Average, 30 days"
                        size="lg"
                        value={avgNow != null ? avgNow.toFixed(1) : '\u2014'}
                        unit={avgNow != null ? '%' : undefined}
                        chip={avgDelta != null ? <Chip tone={avgDelta >= 0 ? 'up' : 'down'}>{avgDelta >= 0 ? '\u2191' : '\u2193'} {Math.abs(avgDelta).toFixed(1)}</Chip> : undefined}
                        sub={recentAvg ? `Across ${recentAvg.exams_with_results} ${recentAvg.exams_with_results === 1 ? copy.one : copy.many} with results` : undefined}
                    />
                </div>
            )}

            <PanelCard aria-label={copy.region}>
                {/* Filters */}
                <div className="mb-1.5 flex flex-wrap items-center gap-2.5 border-b border-hairline pb-[18px]">
                    {counts && (
                        <Segmented
                            scroll
                            label="Status"
                            value={statusKey}
                            onChange={(k) => { setCurrentPage(1); setStatusKey(k); }}
                            options={STATUS_TABS.map((t) => ({
                                key: t.key,
                                label: t.label,
                                count: t.key === 'all' ? counts.active + counts.draft
                                    : t.key === 'active' ? counts.active
                                    : t.key === 'draft' ? counts.draft
                                    : t.key === 'this_week' ? counts.due_this_week
                                    : counts.past_due,
                            }))}
                        />
                    )}
                    <span className="flex-1" />
                    {examsIsFetching && <span aria-hidden="true" className="text-muted"><Spinner size="sm" /></span>}
                    <div className={`${FIELD_WRAP} w-full sm:w-auto`}>
                        <Search size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
                        <input
                            type="search"
                            aria-label={`Search ${copy.many}`}
                            value={search}
                            onChange={(e) => { setCurrentPage(1); setSearch(e.target.value); }}
                            placeholder={`Search ${copy.many}`}
                            className={`${FIELD_INPUT} sm:w-[180px]`}
                        />
                    </div>
                </div>
                <div className="mb-2 mt-3.5 flex flex-wrap items-center gap-x-2.5 gap-y-2">
                    <span className={FIELD_LABEL}>{isUniversityFlow ? 'Department' : 'Grade'}</span>
                    <Segmented
                        scroll
                        label={isUniversityFlow ? 'Filter by department' : 'Filter by grade'}
                        value={nowGrade === 0 ? 'all' : String(nowGrade)}
                        onChange={onGradeChange}
                        options={gradeOptions}
                    />
                    {fieldsData != null && fieldsData.length === 0 && (
                        <p className="text-sm text-muted">No departments found.</p>
                    )}
                    {fieldsData != null ? (
                        <>
                            <span className={FIELD_LABEL}>Academic year</span>
                            <Segmented
                                scroll
                                label="Filter by academic year"
                                value={filterAcademicYearId == null ? 'all' : String(filterAcademicYearId)}
                                onChange={(k) => { setCurrentPage(1); setFilterAcademicYearId(k === 'all' ? null : Number(k)); }}
                                options={[{ key: 'all', label: 'All academic years' }, ...(filterAcademicYears ?? []).map((y) => ({ key: String(y.id), label: y.name }))]}
                            />
                            {(filterCourses?.length ?? 0) > 30 ? (
                                <div className={`${FIELD_WRAP} w-full sm:w-auto sm:min-w-[200px]`}>
                                    <label htmlFor="exam-filter-course" className={FIELD_LABEL}>Course</label>
                                    <select
                                        id="exam-filter-course"
                                        value={nowSubject}
                                        onChange={(e) => { setCurrentPage(1); setNowSubject(e.target.value); }}
                                        className={FIELD_INPUT}
                                    >
                                        <option value="0">All courses</option>
                                        {filterCourses?.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <>
                                    <span className={FIELD_LABEL}>Course</span>
                                    <Segmented
                                        scroll
                                        label="Filter by course"
                                        value={nowSubject}
                                        onChange={(k) => { setCurrentPage(1); setNowSubject(k); }}
                                        options={[{ key: '0', label: 'All courses' }, ...(filterCourses ?? []).map((c) => ({ key: String(c.id), label: c.name }))]}
                                    />
                                </>
                            )}
                        </>
                    ) : nowGrade !== 0 && (
                        <>
                            <span className={FIELD_LABEL}>Subject</span>
                            <Segmented
                                scroll
                                label="Filter by subject"
                                value={nowSubject}
                                onChange={(k) => { setCurrentPage(1); setNowSubject(k); }}
                                options={[
                                    { key: '0', label: 'All' },
                                    ...(subjectIsFetched ? (subjectData?.subjects ?? []) : []).map((elem: any) => ({ key: String(elem['id']), label: String(elem['subject_name']) })),
                                ]}
                            />
                        </>
                    )}
                    {examsStatus === 'success' && (
                        <span className="ml-auto text-[13px] text-muted">{visibleExams.length > 0 ? `${(currentPage - 1) * 10 + 1}\u2013${(currentPage - 1) * 10 + visibleExams.length}` : '0'} of {totalExamsCount} {copy.many}</span>
                    )}
                </div>
                {!isUniversityFlow && nowGrade === 0 && (
                    <p className="mb-2 text-xs text-muted">Pick a grade to filter by subject.</p>
                )}

                {/* List */}
                {examsStatus === 'pending' || examsIsFetching && activeExams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-4 py-24" role="status">
                        <span aria-hidden="true" className="text-muted"><Spinner size="md" /></span>
                        <p className="text-sm text-muted">Loading {copy.many}…</p>
                    </div>
                ) : examsStatus === 'error' ? (
                    <ErrorPanel title={forbidden ? `You don't have access to this ${copy.one} list` : `Could not load ${copy.many}`} body={forbidden ? 'Ask a school administrator to change your role if you need it.' : 'Check your connection and try again.'} onRetry={() => { if (adminSource) setRefreshKey((k) => k + 1); else examRefetch(); }} />
                ) : visibleExams.length === 0 ? (
                    <EmptyState
                        icon={<FileText />}
                        title={`No ${copy.many} found`}
                        body={`Try a different search or filter, or add a new ${copy.one}.`}
                        action={<Button variant="ghost" onClick={clearFilters}>Clear filters</Button>}
                    />
                ) : (
                    <div>
                        {deleteError && (
                            <p role="alert" className="mb-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{deleteError}</p>
                        )}
                        <Table minWidth={1040}>
                            <thead>
                                <tr>
                                    <Th>{kind === 'exam' ? 'Exam' : kind === 'worksheet' ? 'Worksheet' : 'Homework'}</Th>
                                    <Th>Subject · grade</Th>
                                    <Th>Assigned to</Th>
                                    <Th align="right">Qs</Th>
                                    <Th>Due</Th>
                                    <Th align="right">Took</Th>
                                    <Th className="w-[130px]">Average</Th>
                                    <Th>Status</Th>
                                    <Th align="right"><span className="sr-only">Actions</span></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleExams.map((exam: any) => {
                                    const field = (k: string) => exam[k];
                                    const shared = isSharedExam(exam);
                                    const isActive = String(exam.status ?? '').toLowerCase() === 'active' || exam.status === 'published';
                                    const due = parseDue(exam.due_at, exam.due_date);
                                    const dueSoon = due != null && due.getTime() >= startOfToday && due.getTime() <= sevenDaysOut;
                                    const hasDue = Boolean(exam.due_at || exam.due_date);
                                    const dueText = exam.due_at
                                        ? withTime(exam.due_at, { day: 'numeric', month: 'short' })
                                        : (exam.due_date ?? 'No due date');
                                    const took = field('took');
                                    const assigned = field('assigned');
                                    const avg = field('avg_score');
                                    const examSet = field('exam_set') as { id: number; name: string; form_label: string } | null | undefined;
                                    const toMark = Number(field('to_mark') ?? 0);
                                    return (
                                        <tr key={exam.id}>
                                            <Td className="min-w-[250px] max-w-[360px]">
                                                <Link to={`/exams/exam-detail/${exam.id}`} className="block break-words font-semibold text-ink no-underline hover:underline" title={exam.name}>{exam.name}</Link>
                                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                                    {exam.time_given ? (
                                                        <Tag><Clock size={12} strokeWidth={1.8} aria-hidden="true" />{exam.time_given} min</Tag>
                                                    ) : null}
                                                    <KindBadge kind={exam.kind} />
                                                    {field('is_generated') === true && <Tag>AI generated</Tag>}
                                                    {toMark > 0 && (
                                                        <Badge tone="warning" size="sm">{toMark} to mark</Badge>
                                                    )}
                                                    {exam.delivery === 'paper' && (
                                                        <Tag><Term k="delivery">Paper</Term></Tag>
                                                    )}
                                                    {shared && (
                                                        <Tag className="max-w-full">
                                                            <span className="truncate">Shared · {exam.institution_name || 'another institution'}</span>
                                                        </Tag>
                                                    )}
                                                    {examSet && (
                                                        <Tag tone="mist" to={shared ? undefined : `/exams/exam-set/${examSet.id}`}>
                                                            <Layers size={12} strokeWidth={1.8} aria-hidden="true" />Form {examSet.form_label} · {examSet.name}
                                                        </Tag>
                                                    )}
                                                </div>
                                            </Td>
                                            <Td>
                                                <span className="block font-medium">{exam.sub || '—'}</span>
                                                {exam.grade && <span className="text-xs text-muted">{/^grade\s/i.test(String(exam.grade)) ? exam.grade : `Grade ${exam.grade}`}</span>}
                                            </Td>
                                            <Td className="whitespace-nowrap text-[13px] text-muted" title="Assigned to">
                                                {scopeLabel(
                                                    (exam.assignment_scope ?? 'grade_subject') as ExamAssignmentScope,
                                                    exam.grade ?? null,
                                                    exam.sub ?? null,
                                                    (exam.assigned_student_ids ?? []).length,
                                                )}
                                            </Td>
                                            <Td align="right" className="whitespace-nowrap font-mono font-medium">
                                                {exam.no_ques}
                                                <span className="sr-only"> questions</span>
                                            </Td>
                                            <Td className="whitespace-nowrap" title="Due">
                                                {dueSoon ? <Badge tone="accent">{dueText}</Badge> : <span className={hasDue ? 'font-mono font-medium text-ink' : 'text-muted'}>{dueText}</span>}
                                            </Td>
                                            <Td align="right" className="whitespace-nowrap font-mono font-medium">
                                                {typeof took === 'number' && typeof assigned === 'number' ? `${took} / ${assigned}` : <span className="text-muted">—</span>}
                                            </Td>
                                            <Td>
                                                <span className="flex items-center gap-2">
                                                    <Track value={typeof avg === 'number' ? avg : 0} tone={typeof avg === 'number' && avg < 50 ? 'accent' : 'slate'} label={`Average for ${exam.name}`} className="flex-1" />
                                                    <span className="w-11 text-right font-mono font-medium">{typeof avg === 'number' ? `${avg.toFixed(1)}%` : '—'}</span>
                                                </span>
                                            </Td>
                                            <Td className="whitespace-nowrap">
                                                <Badge tone={isActive ? 'success' : 'draft'} size="sm">{exam.status}</Badge>
                                            </Td>
                                            <Td className="whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-1">
                                                    {shared ? (
                                                        <RowAction label={`View ${copy.one}`} onClick={() => navigate(`/exams/exam-detail/${exam.id}`)} hoverClass="hover:border-line-strong hover:bg-ground hover:text-ink">
                                                            <Eye size={18} strokeWidth={1.8} aria-hidden="true" />
                                                        </RowAction>
                                                    ) : (
                                                        <>
                                                            {exam.can_edit === false ? (
                                                                // Another teacher's work: open it read-only (owner rule).
                                                                <RowAction label={`View ${copy.one}`} onClick={() => navigate(`/exams/exam-detail/${exam.id}`)} hoverClass="hover:border-line-strong hover:bg-ground hover:text-ink">
                                                                    <Eye size={18} strokeWidth={1.8} aria-hidden="true" />
                                                                </RowAction>
                                                            ) : (
                                                                <RowAction label={`Edit ${copy.one}`} onClick={() => navigate(kind === 'exam' ? `/exams/exam-detail/${exam.id}` : `/worksheets/${exam.id}/edit`)} hoverClass="hover:border-line-strong hover:bg-ground hover:text-ink">
                                                                    <Pencil size={18} strokeWidth={1.8} aria-hidden="true" />
                                                                </RowAction>
                                                            )}
                                                            <Link to={`/exams/report/${exam.id}`} aria-label="Report" title="Open report" className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-transparent text-muted-2 outline-none transition-colors hover:border-line-strong hover:bg-ground hover:text-ink focus-visible:ring-2 focus-visible:ring-accent">
                                                                <BarChart3 size={18} strokeWidth={1.8} aria-hidden="true" />
                                                            </Link>
                                                            <RowAction label={`Delete ${copy.one}`} onClick={() => otherSchoolGuard(exam, () => { setExamToDelete(exam); setIsDeleteModalOpen(true); })} hoverClass="hover:border-danger-line hover:bg-danger-bg hover:text-danger-ink">
                                                                <Trash2 size={18} strokeWidth={1.8} aria-hidden="true" />
                                                            </RowAction>
                                                        </>
                                                    )}
                                                </div>
                                            </Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Footer: read-only note + pagination (design `.pg`) */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4">
                    <p className="text-[13px] text-muted">Shared {copy.many} from other institutions are read-only. Drafts shared with you stay hidden.</p>
                    {examsStatus === 'success' && (
                        <Pager page={currentPage} pageCount={totalPages} onPage={onPageChange} />
                    )}
                </div>
            </PanelCard>

            {kind === 'exam' && <ExamSetsCard />}


            {otherSchoolDialog}
            <ConfirmDialog
                isOpen={isDeleteModalOpen}
                title={`Delete this ${copy.one}?`}
                body={`"${examToDelete?.name ?? ''}" and its questions will be permanently deleted. This cannot be undone.`}
                confirmLabel={isDeleting ? 'Deleting…' : `Delete ${copy.one}`}
                onConfirm={() => { if (!isDeleting) handleDeleteExam(); }}
                onCancel={() => { if (!isDeleting) setIsDeleteModalOpen(false); }}
            />
        </>
    )
}

export default ExamListComponent
